import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { checkoutAuthHeaders } from '../checkout/checkoutAuthHeaders';
import CheckoutField from '../checkout/CheckoutField';
import { useIndiaPincodeLookup } from '../checkout/useIndiaPincodeLookup';
import { INDIAN_STATES } from '../data/indianStates';

const LABEL_PRESETS = ['Home', 'Office', 'Other'];

const emptyForm = {
  labelPreset: 'Home',
  labelCustom: '',
  name: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  is_default: true,
};

function parseLabelFields(label) {
  const t = String(label || '').trim();
  if (LABEL_PRESETS.includes(t)) {
    return { labelPreset: t, labelCustom: '' };
  }
  if (!t || t === 'Home') {
    return { labelPreset: 'Home', labelCustom: '' };
  }
  return { labelPreset: 'Other', labelCustom: t };
}

function resolveLabel(form) {
  if (form.labelPreset === 'Other') {
    return form.labelCustom.trim() || 'Other';
  }
  return form.labelPreset;
}

export default function AccountAddressBook({ embedded = false }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [cityTouched, setCityTouched] = useState(false);
  const [stateTouched, setStateTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgTone, setMsgTone] = useState('muted');

  const pinLookup = useIndiaPincodeLookup(form.pincode);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/customer.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...checkoutAuthHeaders() },
        body: JSON.stringify({ action: 'list_addresses' }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not load addresses.');
        setAddresses([]);
      } else {
        setAddresses(Array.isArray(d.addresses) ? d.addresses : []);
      }
    } catch {
      setError('Could not load addresses.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!editing) return;
    if (pinLookup.status !== 'ok') return;
    setForm((f) => {
      const next = { ...f };
      if (!cityTouched) next.city = pinLookup.city;
      if (!stateTouched) next.state = pinLookup.stateName;
      return next;
    });
  }, [editing, pinLookup.status, pinLookup.city, pinLookup.stateName, cityTouched, stateTouched]);

  const pinHint = useMemo(() => {
    if (pinLookup.status === 'loading') return 'Looking up PIN…';
    if (pinLookup.status === 'ok' && pinLookup.locality) return `Area: ${pinLookup.locality}`;
    if (pinLookup.status === 'error') return pinLookup.message;
    return '6-digit postal PIN — city and state fill automatically';
  }, [pinLookup]);

  const resetTouched = () => {
    setCityTouched(false);
    setStateTouched(false);
  };

  const startNew = () => {
    setEditing('new');
    setForm(emptyForm);
    resetTouched();
    setMsg('');
  };

  const startEdit = (a) => {
    const { labelPreset, labelCustom } = parseLabelFields(a.label);
    setEditing(a.id);
    setForm({
      labelPreset,
      labelCustom,
      name: a.name || '',
      phone: a.phone || '',
      address: a.address || '',
      city: a.city || '',
      state: a.state || '',
      pincode: String(a.pincode || '').replace(/\D/g, '').slice(0, 6),
      is_default: Boolean(a.is_default),
    });
    resetTouched();
    setMsg('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setMsg('');
    resetTouched();
  };

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setMsgTone('muted');
    try {
      const r = await fetch('/api/customer.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...checkoutAuthHeaders() },
        body: JSON.stringify({
          action: 'save_address',
          id: editing === 'new' ? undefined : editing,
          label: resolveLabel(form),
          name: form.name,
          phone: form.phone,
          address: form.address,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          is_default: form.is_default,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setMsg(d.error || 'Could not save.');
        setMsgTone('danger');
        return;
      }
      setMsg('Address saved.');
      setMsgTone('success');
      setEditing(null);
      resetTouched();
      await load();
    } catch {
      setMsg('Could not save address.');
      setMsgTone('danger');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Remove this address?')) return;
    setBusy(true);
    try {
      await fetch('/api/customer.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...checkoutAuthHeaders() },
        body: JSON.stringify({ action: 'delete_address', id }),
      });
      if (editing === id) cancelEdit();
      await load();
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div className="address-book">
      {loading ? <p className="address-book__status small text-muted">Loading your addresses…</p> : null}
      {error ? (
        <p className="address-book__status small text-danger" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && addresses.length === 0 && !editing ? (
        <div className="address-book__empty">
          <p className="address-book__empty-title">No saved addresses yet</p>
          <p className="address-book__empty-text">Add one for faster checkout — we will remember it on your account.</p>
        </div>
      ) : null}

      {addresses.length > 0 ? (
        <ul className="address-book__list list-unstyled mb-0">
          {addresses.map((a) => (
            <li
              key={a.id}
              className={`address-book__card${editing === a.id ? ' address-book__card--active' : ''}`}
            >
              <div className="address-book__card-head">
                <span className="address-book__card-label">{a.label}</span>
                {a.is_default ? <span className="address-book__card-badge">Default</span> : null}
              </div>
              <p className="address-book__card-name">{a.name}</p>
              <p className="address-book__card-phone">{a.phone}</p>
              <p className="address-book__card-lines">
                {a.address}
                <br />
                {a.city}, {a.state} {a.pincode}
              </p>
              <div className="address-book__card-actions">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm rounded-pill"
                  onClick={() => startEdit(a)}
                  disabled={busy || (editing && editing !== a.id)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill"
                  onClick={() => onDelete(a.id)}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {!editing ? (
        <button type="button" className="btn btn-primary rounded-pill address-book__add-btn" onClick={startNew}>
          + Add new address
        </button>
      ) : (
        <form onSubmit={onSave} className="address-form-card">
          <header className="address-form-card__head">
            <h3 className="address-form-card__title">{editing === 'new' ? 'Add address' : 'Edit address'}</h3>
            <p className="address-form-card__lead mb-0">
              Enter your 6-digit PIN first — we will fill city and state, same as checkout.
            </p>
          </header>

          <fieldset className="address-form-card__section">
            <legend className="address-form-card__legend">Address type</legend>
            <div className="address-label-chips" role="radiogroup" aria-label="Address type">
              {LABEL_PRESETS.map((preset) => (
                <label
                  key={preset}
                  className={`address-label-chip${form.labelPreset === preset ? ' address-label-chip--active' : ''}`}
                >
                  <input
                    type="radio"
                    name="address-label-preset"
                    className="address-label-chip__input visually-hidden"
                    value={preset}
                    checked={form.labelPreset === preset}
                    onChange={() => setForm((f) => ({ ...f, labelPreset: preset }))}
                  />
                  <span className="address-label-chip__text">{preset}</span>
                </label>
              ))}
            </div>
            {form.labelPreset === 'Other' ? (
              <CheckoutField id="addr-label-custom" label="Custom label" required>
                <input
                  id="addr-label-custom"
                  className="form-control checkout-input"
                  placeholder="e.g. Parents, Warehouse"
                  value={form.labelCustom}
                  onChange={(e) => setForm((f) => ({ ...f, labelCustom: e.target.value }))}
                  maxLength={64}
                  required
                />
              </CheckoutField>
            ) : null}
          </fieldset>

          <fieldset className="address-form-card__section">
            <legend className="address-form-card__legend">Contact</legend>
            <div className="row g-3">
              <div className="col-md-6">
                <CheckoutField id="addr-name" label="Full name" required>
                  <input
                    id="addr-name"
                    className="form-control checkout-input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    autoComplete="name"
                    required
                    disabled={busy}
                  />
                </CheckoutField>
              </div>
              <div className="col-md-6">
                <CheckoutField id="addr-phone" label="Mobile number" required hint="For delivery updates">
                  <input
                    id="addr-phone"
                    className="form-control checkout-input"
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    autoComplete="tel"
                    required
                    disabled={busy}
                  />
                </CheckoutField>
              </div>
            </div>
          </fieldset>

          <fieldset className="address-form-card__section">
            <legend className="address-form-card__legend">Location</legend>
            <div className="row g-3">
              <div className="col-md-4">
                <CheckoutField id="addr-pincode" label="PIN code" required hint={pinHint}>
                  <input
                    id="addr-pincode"
                    className="form-control checkout-input checkout-input--pin"
                    inputMode="numeric"
                    maxLength={6}
                    value={form.pincode}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }));
                      resetTouched();
                    }}
                    autoComplete="postal-code"
                    required
                    disabled={busy}
                  />
                </CheckoutField>
              </div>
              <div className="col-md-4">
                <CheckoutField id="addr-city" label="City / district" required>
                  <input
                    id="addr-city"
                    className="form-control checkout-input"
                    value={form.city}
                    onChange={(e) => {
                      setCityTouched(true);
                      setForm((f) => ({ ...f, city: e.target.value }));
                    }}
                    autoComplete="address-level2"
                    required
                    disabled={busy}
                  />
                </CheckoutField>
              </div>
              <div className="col-md-4">
                <CheckoutField id="addr-state" label="State" required>
                  <select
                    id="addr-state"
                    className="form-select checkout-input"
                    value={form.state}
                    onChange={(e) => {
                      setStateTouched(true);
                      setForm((f) => ({ ...f, state: e.target.value }));
                    }}
                    autoComplete="address-level1"
                    required
                    disabled={busy}
                  >
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </CheckoutField>
              </div>
            </div>
          </fieldset>

          <fieldset className="address-form-card__section address-form-card__section--last">
            <legend className="address-form-card__legend">Street</legend>
            <CheckoutField id="addr-street" label="Street address" required>
              <textarea
                id="addr-street"
                className="form-control checkout-input"
                rows={3}
                placeholder="House no., street, landmark"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                autoComplete="street-address"
                required
                disabled={busy}
              />
            </CheckoutField>
            <label className="address-form-card__default store-check--inline">
              <input
                type="checkbox"
                className="form-check-input"
                checked={form.is_default}
                onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                disabled={busy}
              />
              <span className="form-check-label">Use as default address at checkout</span>
            </label>
          </fieldset>

          {msg ? (
            <p
              className={`address-form-card__msg small mb-0${
                msgTone === 'danger' ? ' text-danger' : msgTone === 'success' ? ' text-success' : ' text-muted'
              }`}
              role="status"
            >
              {msg}
            </p>
          ) : null}

          <div className="address-form-card__actions">
            <button type="submit" className="btn btn-primary rounded-pill" disabled={busy}>
              {busy ? 'Saving…' : 'Save address'}
            </button>
            <button type="button" className="btn btn-outline-secondary rounded-pill" onClick={cancelEdit} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );

  if (embedded) return body;

  return (
    <section className="account-password" aria-labelledby="account-address-heading">
      <h2 id="account-address-heading" className="account-section-label">
        Delivery addresses
      </h2>
      <div className="account-password-card">{body}</div>
    </section>
  );
}
